/**
 * ColorScheme - Color scheme selector with mini gradient previews.
 *
 * Renders 5 tab-style buttons, one per `ColorSchemeId`. Each button shows a
 * small horizontal gradient built from the scheme's color stops so the user
 * can preview the palette before selecting it.
 */
import { ColorSchemeId } from '../../types/enums';
import { COLOR_SCHEMES } from '../../constants/design-tokens';

export interface ColorSchemeProps {
  currentScheme: ColorSchemeId;
  onSchemeChange: (scheme: ColorSchemeId) => void;
}

const SCHEME_ORDER: ColorSchemeId[] = [
  ColorSchemeId.Default,
  ColorSchemeId.Ice,
  ColorSchemeId.Viridis,
  ColorSchemeId.Inferno,
  ColorSchemeId.Colorblind,
];

/** Build a CSS linear-gradient string from the scheme's stops. */
function gradientFor(schemeId: ColorSchemeId): string {
  const scheme = COLOR_SCHEMES[schemeId];
  const stops = scheme.stops as [number, string][];
  const parts = stops.map(([pos, color]) => `${color} ${(pos * 100).toFixed(0)}%`);
  return `linear-gradient(to right, ${parts.join(', ')})`;
}

export default function ColorScheme({ currentScheme, onSchemeChange }: ColorSchemeProps) {
  const containerStyle: React.CSSProperties = {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  };

  const buttonBase: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    padding: '6px 10px',
    borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 150ms ease',
  };

  return (
    <div style={containerStyle} role="group" aria-label="配色方案选择">
      {SCHEME_ORDER.map((schemeId) => {
        const scheme = COLOR_SCHEMES[schemeId];
        const active = schemeId === currentScheme;
        const buttonStyle: React.CSSProperties = {
          ...buttonBase,
          borderColor: active ? 'var(--color-ice)' : 'var(--border-default)',
          background: active ? 'rgba(34, 211, 238, 0.08)' : 'transparent',
        };
        const labelStyle: React.CSSProperties = {
          fontSize: 11,
          color: active ? 'var(--color-ice)' : 'var(--text-secondary)',
          fontWeight: active ? 600 : 400,
        };
        const previewStyle: React.CSSProperties = {
          width: 64,
          height: 10,
          borderRadius: 3,
          background: gradientFor(schemeId),
          border: '1px solid var(--border-subtle)',
        };
        return (
          <button
            key={schemeId}
            type="button"
            style={buttonStyle}
            onClick={() => onSchemeChange(schemeId)}
            aria-pressed={active}
            aria-label={`配色方案: ${scheme.name}`}
          >
            <span style={previewStyle} />
            <span style={labelStyle}>{scheme.name}</span>
          </button>
        );
      })}
    </div>
  );
}
